import { useState } from 'react'
import Modal from '../components/Modal'
import { ErrorText, Field, Row, Select, Text, TextArea, TimeZoneSelect } from '../components/Form'
import {
  createItineraryItem,
  updateItineraryItem,
  deleteItineraryItem,
} from '../lib/mutations'
import type { ItineraryDraft } from '../lib/mutations'
import { ITINERARY_TYPES } from '../lib/itineraryTypes'
import { browserTimeZone, toDateInputValue, toTimeInputValue } from '../lib/zonedTime'
import type { ItineraryItem, WithId } from '../types/models'

export default function ItineraryDialog({
  open,
  item,
  tourId,
  userId,
  defaultDate,
  onClose,
}: {
  open: boolean
  item?: WithId<ItineraryItem> | null
  tourId: string
  userId: string
  defaultDate?: string
  onClose: () => void
}) {
  const [draft, setDraft] = useState<ItineraryDraft>(() => initialDraft(item, defaultDate))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const patch = (next: Partial<ItineraryDraft>) =>
    setDraft((previous) => ({ ...previous, ...next }))

  const valid = draft.title.trim().length > 0 && draft.date.length > 0

  // Show timings belong to their show — editing one here would drift from the
  // fields on the show document, and the next show edit would overwrite it.
  const belongsToShow = item?.isShowTiming === true

  async function save() {
    if (!valid) return
    setBusy(true)
    setError(null)
    try {
      if (item) await updateItineraryItem(item.id, draft, tourId, item.ownerId || userId)
      else await createItineraryItem(draft, tourId, userId)
      onClose()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!item) return
    setBusy(true)
    setError(null)
    try {
      await deleteItineraryItem(item.id)
      onClose()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      title={item ? 'Edit item' : 'Add to itinerary'}
      onClose={onClose}
      footer={
        <>
          {item && (
            <button
              className="btn btn-danger"
              disabled={busy}
              style={{ marginRight: 'auto' }}
              onClick={() => setConfirmingDelete(true)}
            >
              Delete
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={!valid || busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="stack">
        {belongsToShow && (
          <p
            className="t-caption"
            style={{
              padding: '9px 12px',
              borderRadius: 'var(--radius-control)',
              background: 'var(--accent-wash)',
              color: 'var(--accent-text)',
            }}
          >
            This is part of a show's running order. Changing the time here will be overwritten the
            next time the show is edited — change it on the show instead.
          </p>
        )}

        <Field label="Title">
          <Text
            value={draft.title}
            onChange={(value) => patch({ title: value })}
            autoFocus
          />
        </Field>

        <Field label="Type">
          <Select
            value={draft.type}
            onChange={(value) => patch({ type: value })}
            options={ITINERARY_TYPES.map((entry) => ({ value: entry.id, label: entry.label }))}
          />
        </Field>

        <Row>
          <Field label="Date">
            <Text type="date" value={draft.date} onChange={(value) => patch({ date: value })} />
          </Field>
          <Field label="Time" hint="Leave blank if it isn't known yet.">
            <Text type="time" value={draft.time} onChange={(value) => patch({ time: value })} />
          </Field>
        </Row>

        <Field label="Timezone" hint="Where this happens, not where you are.">
          <TimeZoneSelect value={draft.timezone} onChange={(value) => patch({ timezone: value })} />
        </Field>

        <Field label="Subtitle">
          <Text
            value={draft.subtitle ?? ''}
            onChange={(value) => patch({ subtitle: value })}
          />
        </Field>

        <Field label="Notes">
          <TextArea value={draft.notes ?? ''} onChange={(value) => patch({ notes: value })} />
        </Field>

        <ErrorText>{error}</ErrorText>

        {confirmingDelete && item && (
          <div
            className="stack"
            style={{
              gap: 8,
              padding: 14,
              borderRadius: 'var(--radius-control)',
              background: 'color-mix(in srgb, var(--destructive) 10%, transparent)',
            }}
          >
            <strong>Delete “{item.title}”?</strong>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn-danger" onClick={() => void remove()} disabled={busy}>
                {busy ? 'Deleting…' : 'Delete item'}
              </button>
              <button className="btn btn-quiet" onClick={() => setConfirmingDelete(false)}>
                Keep it
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function initialDraft(
  item: WithId<ItineraryItem> | null | undefined,
  defaultDate?: string,
): ItineraryDraft {
  if (!item) {
    return {
      title: '',
      type: 'custom',
      date: defaultDate ?? toDateInputValue(new Date()),
      time: '',
      timezone: browserTimeZone(),
      subtitle: '',
      notes: '',
    }
  }

  const zone = item.timezone ?? browserTimeZone()
  return {
    title: item.title,
    type: item.type,
    date: toDateInputValue(item.timeUTC.toDate(), zone),
    // An item still waiting for a time opens with the field empty rather than
    // showing the midnight it was parked at.
    time: item.needsTime ? '' : toTimeInputValue(item.timeUTC.toDate(), zone),
    timezone: zone,
    subtitle: item.subtitle ?? '',
    notes: item.notes ?? '',
  }
}
